import { DonateFormItem } from './DonateCommon';
import Button from '@/components/Button';
import ErrorMessage from '@/components/Form/ErrorMessage';
import InputNumber from '@/components/Input/InputNumber';
import InputRadio, { InputRadioGroup } from '@/components/Input/InputRadio';
import InputText from '@/components/Input/InputText';
import Select from '@/components/Select';
import { NotiBox, NotiItem, NotiList } from '@/components/Text/TextNoti';
import { H4 } from '@/components/Titles';
import { breakpoint } from '@/helpers/BreakpointHelper';
import {
  PAYMENT_MIN_AMOUNT,
  PAYMENT_MIN_AMOUNT_MSG,
  PAYMENT_HIGH_AMOUNT,
  PAYMENT_HIGH_AMOUNT_MSG,
  PAYMENT_PG_MAX_AMOUNT,
  SUPPORT_TERMS,
  SUPPORT_TERM_ONCE,
  SUPPORT_TERM_REGULAR,
  SOLICIT_CODE_CHECKOUT_INMEMORY,
  useFindSupportCategory,
  useSupportCategoriesForPayment,
} from '@/helpers/SupportHelper';
import { graphql, useStaticQuery, navigate } from 'gatsby';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import styled from 'styled-components';

const FormContainer = styled.div`
  ${InputRadioGroup} {
    ${breakpoint(`tablet`)} {
      ${InputText} {
        height: 44px;
      }
    }
  }
`;

const FormTip = styled.div<{
  type?: string;
}>`
  padding: 22px 20px;
  ${({ type }) => {
    if (type === `blue`) {
      return `
        background-color: #e2f0f6;
        color: #1cabe2;
        `;
    }
    if (type === `gray`) {
      return `
        background-color: #f8f9fa;
        color: #2d2926;
        `;
    }
    return ``;
  }}
`;

const FormTotal = styled.div`
  position: relative;
  padding: 32px 0;
  margin: 56px 0 80px 0;
  border-top: 1px solid #b7b8ba;
  border-bottom: 1px solid #b7b8ba;
  ${H4} {
    position: absolute;
    left: 0;
    bottom: 16px;
    margin: 0;
  }
`;
const FormTotalPrice = styled.div`
  font-size: 32px;
  font-weight: bold;
  color: #1cabe2;
  text-align: right;
`;

const TitleWithText = styled.div`
  display: flex;
  div {
    padding: 0 0 16px 10px;
    display: flex;
    align-items: center;
  }
`;

// 초기설정값 타입 (step1 스킵용)
export interface SupportInitialValues {
  supportAmount?: string;
  supportAmountInput?: string;
  supportCategory?: string;
  supportTerm?: string;
  solicitCode?: string;
  sfchannel?: string;
  // 리워드캠페인옵션 (후원모듈X)
  supportAmount1?: number;
  supportAmount2?: number;
  supportAmount3?: number;
  supportAmount4?: number;
  supportAmount5?: number;
  minAmount?: number;
}

export interface SupportStep1Data {
  supportTerm: keyof typeof SUPPORT_TERMS;
  // 저장시에 category와 매핑
  supportCategoryIndex: string | number;
  supportAmount: string;
  supportAmountInput: string;
}

interface DonateStep1Props {
  onSubmit: (data: SupportStep1Data) => void;
  initialValues: SupportInitialValues;
  isSupportModule: boolean; // 후원모듈을 통해서 유입했는지 여부
}

const DonateStep1: FC<DonateStep1Props> = React.memo(
  ({ onSubmit, initialValues, isSupportModule }) => {
    // 쿼리스트링에서 넘어온 카테고리 찾기
    const querySupportCategoryIndex = useFindSupportCategory(
      initialValues.supportCategory,
      initialValues.solicitCode,
    )?.indexNumber;
    const {
      register,
      handleSubmit,
      watch,
      setValue,
      clearErrors,
      formState: { errors },
      setError,
      control,
    } = useForm<SupportStep1Data>({
      defaultValues: {
        // 잘못된 값이 넘어오더라도 regular로 처리
        supportTerm:
          initialValues.supportTerm === SUPPORT_TERM_ONCE
            ? SUPPORT_TERM_ONCE
            : SUPPORT_TERM_REGULAR,
        supportAmount: initialValues.supportAmountInput
          ? ``
          : initialValues.supportAmount || '',
        supportAmountInput: initialValues.supportAmountInput || ``,
        supportCategoryIndex: querySupportCategoryIndex || ``,
      },
    });

    // 후원코드 후원금액 가져오는 훅
    const useAllSupportCodeAmount = (): GatsbyTypes.SupportCodeAmount[] => {
      const {
        allSupportCodeAmount: { nodes: supportCodeAmount },
      } = useStaticQuery<{
        allSupportCodeAmount: { nodes: GatsbyTypes.SupportCodeAmount[] };
      }>(graphql`
        query {
          allSupportCodeAmount {
            nodes {
              indexNumber
              codeBaseIndexNumber
              categoryCode
              solicitCode
              supportCode
              supportAmount1
              supportAmount2
              supportAmount3
              supportAmount4
              supportAmount5
              sfChannel
            }
          }
        }
      `);
      return supportCodeAmount;
    };

    const supportTerm = watch(`supportTerm`);
    const supportAmount = watch(`supportAmount`, `0`);
    const supportAmountInput = watch(`supportAmountInput`);
    // 총 후원금액
    const finalAmount = useMemo(() => {
      /*
      console.log(
        '## useMemo : finalAmount ->> ',
        supportAmount,
        supportAmountInput,
      );
      */
      return Number(supportAmount || supportAmountInput);
    }, [supportAmount, supportAmountInput]);

    const supportCategories = useSupportCategoriesForPayment(supportTerm);
    const supportCodeAmount = useAllSupportCodeAmount();
    const selectedCategory = watch(`supportCategoryIndex`);

    // 선택된 카테고리의 solicitCode
    const selectedSolicitCode = useMemo(() => {
      const category = supportCategories.find(
        (category) => category.indexNumber === Number(selectedCategory),
      );
      return category?.solicitCode || initialValues.solicitCode;
    }, [selectedCategory, supportCategories, initialValues.solicitCode]);

    // 긴급구호 단어가 포함된 경우 SupportPhrase hide
    const isEmergencyCategory = useMemo(() => {
      /*
      console.log(
        '## useMemo : isEmergencyCategory ->> ',
        selectedCategory,
        supportCategories,
      );
      */
      const emergencyCategory = supportCategories.filter(
        (category) => category.indexNumber === Number(selectedCategory),
      );
      if (emergencyCategory[0]?.codeName?.includes('긴급구호')) {
        return true;
      }
      return false;
    }, [selectedCategory, supportCategories]);

    // 기본 후원금액
    const getDefaultAmount = () => {
      if (supportTerm === 'FM01') {
        // 정기후원
        return {
          supportAmount1: 30000,
          supportAmount2: 50000,
          supportAmount3: 100000,
          supportAmount4: 200000,
          supportAmount5: 300000,
        };
      }
      // 일시후원
      if (supportTerm === 'FM02') {
        return {
          supportAmount1: 50000,
          supportAmount2: 100000,
          supportAmount3: 200000,
          supportAmount4: 300000,
          supportAmount5: 500000,
        };
      }
      return {};
    };

    // 후원금액표 설정 (후원분야 변경 혹은 사이트내 바로가기)
    // 초기 설정에 해당하는 후원금액표가 없을 경우 기본 후원금액표 반환
    const selectedAmount = useMemo(() => {
      /*
      console.log(
        '## useMemo : selectedAmount ->> ',
        initialValues,
        selectedCategory,
        supportCodeAmount,
      );
      */
      // 초기 설정 없을 경우 기본 후원금액표 반환
      if (!initialValues) return getDefaultAmount();

      let filteredCodeAmount;
      if (
        initialValues.supportAmount1 &&
        initialValues.supportAmount2 &&
        initialValues.supportAmount3 &&
        initialValues.supportAmount4 &&
        initialValues.supportAmount5
      ) {
        return {
          supportAmount1: initialValues.supportAmount1,
          supportAmount2: initialValues.supportAmount2,
          supportAmount3: initialValues.supportAmount3,
          supportAmount4: initialValues.supportAmount4,
          supportAmount5: initialValues.supportAmount5,
        };
      }
      // WEB-379: sfchannel 파라미터가 있으면 sfChannel 조건도 매칭
      const sfFilter = initialValues.sfchannel
        ? (code: GatsbyTypes.SupportCodeAmount) =>
            (code.sfChannel || '') === initialValues.sfchannel
        : (code: GatsbyTypes.SupportCodeAmount) =>
            !(code.sfChannel);
      if (
        initialValues.supportTerm &&
        initialValues.supportCategory &&
        initialValues.solicitCode
      ) {
        filteredCodeAmount = {
          ...supportCodeAmount.filter(
            (code) =>
              code.categoryCode === initialValues.supportTerm &&
              code.supportCode === initialValues.supportCategory &&
              code.solicitCode === initialValues.solicitCode &&
              sfFilter(code),
          )[0],
        };
      } else if (
        initialValues.supportTerm &&
        initialValues.supportCategory &&
        initialValues.sfchannel
      ) {
        // WEB-379: solicitCode 없이 supportCategory + sfchannel로 매칭
        filteredCodeAmount = {
          ...supportCodeAmount.filter(
            (code) =>
              code.categoryCode === initialValues.supportTerm &&
              code.supportCode === initialValues.supportCategory &&
              sfFilter(code),
          )[0],
        };
      } else {
        filteredCodeAmount = {
          ...supportCodeAmount.filter(
            (code) => code.codeBaseIndexNumber === Number(selectedCategory) &&
              sfFilter(code),
          )[0],
        };
      }
      // 초기 설정에 해당하는 후원금액표가 없을 경우 기본 후원금액표 반환
      console.log(
        '.......',
        filteredCodeAmount?.indexNumber,
        filteredCodeAmount?.supportAmount1,
        initialValues,
      );
      if (filteredCodeAmount?.indexNumber && filteredCodeAmount?.supportAmount1)
        return { ...filteredCodeAmount };
      else return getDefaultAmount();
    }, [
      supportTerm,
      initialValues.solicitCode,
      initialValues.supportCategory,
      initialValues.supportTerm,
      initialValues.supportAmount1,
      initialValues.supportAmount2,
      initialValues.supportAmount3,
      initialValues.supportAmount4,
      initialValues.supportAmount5,
      initialValues.sfchannel,
      selectedCategory,
      supportCodeAmount,
    ]);

    // 기본 후원금액 설정 (후원방법 탭 클릭)
    const defaultAmount = useMemo(() => {
      /*
      console.log(
        '## useMemo : defaultAmount ->> (supportTerm, selectedAmount) ',
        supportTerm,
        selectedAmount,
      );
      */
      return getDefaultAmount();
    }, [supportTerm]);

    // 후원금액 상한선 확인 (후원모듈 : PAYMENT_PG_MAX_AMOUNT / 그외 : PAYMENT_HIGH_AMOUNT)
    // WEB-316: sfchannel=MAJ(고액후원)인 경우 서버 int 최대값(약 21억) 상한 적용
    const PAYMENT_INT_MAX_AMOUNT = 2147483647;
    const isExcessAmount = (amount: number) => {
      if (initialValues.sfchannel === 'MAJ') return amount > PAYMENT_INT_MAX_AMOUNT;
      return (
        (isSupportModule === false && amount >= PAYMENT_HIGH_AMOUNT) ||
        (isSupportModule === true && amount > PAYMENT_PG_MAX_AMOUNT)
      );
    };
    const excessAmountMsg = initialValues.sfchannel === 'MAJ'
      ? '입력 가능한 최대 금액을 초과하였습니다.'
      : PAYMENT_HIGH_AMOUNT_MSG;

    // 후원금액 셋팅
    useEffect(() => {
      /*
      console.log(
        '## useEffect : 1 ',
        initialValues.supportAmount,
        initialValues.supportAmountInput,
        selectedAmount?.supportAmount1?.toString(),
      );
      */
      // 후원금액 기본값 설정
      // selectedAmount?.supportAmount1 값이 0 일 경우 예외처리
      let selectedAmountVal =
        selectedAmount?.supportAmount1 === 0
          ? undefined
          : selectedAmount?.supportAmount1;
      const tmpSupportAmount =
        initialValues.supportAmountInput ||
        (['EM26B', 'CM26H'].includes(selectedSolicitCode) && initialValues.sfchannel !== 'MAJ' ? '50000' : undefined) ||
        (selectedSolicitCode === 'EM26C' && initialValues.sfchannel !== 'MAJ' ? '30000' : undefined) ||
        initialValues.supportAmount ||
        selectedAmountVal?.toString() ||
        selectedAmount?.supportAmount1?.toString();
      /*
      console.log('sssssssssssssssssssssssssssssssss');
      console.log('initialValues (초기데이터)', initialValues);
      console.log('selectedAmount (후원금액표)', selectedAmount);
      console.log('---------------------------------');
      console.log(
        'supportAmount (전송값 >> 초기값 : 선택 후원금, 직접입력 후원금, 임시 초기값)',
        supportAmount,
        supportAmountInput,
        tmpSupportAmount,
      );
      console.log('eeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee');
      */
      // 후원금액 셋팅
      setValue('supportAmount', tmpSupportAmount);
      // supportAmount 금액이 selectedAmount 없을 경우 0원 출력 방지
      if (
        selectedAmount.supportAmount1?.toString() !== tmpSupportAmount &&
        selectedAmount.supportAmount2?.toString() !== tmpSupportAmount &&
        selectedAmount.supportAmount3?.toString() !== tmpSupportAmount &&
        selectedAmount.supportAmount4?.toString() !== tmpSupportAmount &&
        selectedAmount.supportAmount5?.toString() !== tmpSupportAmount
      ) {
        setValue('supportAmountInput', tmpSupportAmount);
      }
    }, [
      initialValues.supportAmount,
      initialValues.supportAmountInput,
      selectedAmount,
      selectedSolicitCode,
      setValue,
    ]);

    const onSubmitForm: SubmitHandler<SupportStep1Data> = useCallback(
      (formData) => {
        if (!finalAmount) {
          setError(`supportAmountInput`, {
            type: `required`,
            message: `후원금액은 필수 입니다`,
            shouldFocus: true,
          });

          return;
        }
        if (isExcessAmount(Number(finalAmount))) {
          setError(`supportAmountInput`, {
            type: `required`,
            message: excessAmountMsg,
            shouldFocus: true,
          });

          return;
        }

        onSubmit(formData);
      },
      [finalAmount, onSubmit, setError],
    );

    const onPressSupportAmount = useCallback(() => {
      setValue(`supportAmountInput`, ``);
      clearErrors(`supportAmountInput`);
    }, [clearErrors, setValue]);

    // 모든 조건이 넘어오면 자동 submit
    useEffect(() => {
      /*
      console.log(
        '## useEffect : 2 ',
        initialValues.supportAmount,
        initialValues.supportAmountInput,
        initialValues.supportCategory,
        initialValues.supportTerm,
      );
      */
      if (
        (initialValues.supportAmount || initialValues.supportAmountInput) &&
        initialValues.supportTerm &&
        initialValues.supportCategory
      ) {
        handleSubmit(onSubmitForm)();
      }
    }, [
      handleSubmit,
      onSubmitForm,
      initialValues.supportAmount,
      initialValues.supportAmountInput,
      initialValues.supportCategory,
      initialValues.supportTerm,
    ]);

    const alerted = useRef(false);
    useEffect(() => {
      const endDate = new Date('2025-11-28T00:00:00+09:00');
      const now = new Date();

      if (
        supportTerm === 'FM02' &&
        initialValues.supportCategory === 'D201' &&
        initialValues.solicitCode === 'RF25A' &&
        now >= endDate &&
        !alerted.current
      ) {
        alerted.current = true;
        alert(
          '마감 기한 경과로 2025 블루스타 나잇 접수가 종료되었습니다. \n관심에 감사드립니다.',
        );
        navigate('/');
      }
    }, [supportTerm, initialValues]);

    return (
      <FormContainer>
        <form onSubmit={handleSubmit(onSubmitForm)}>
          {/* <HR color="#1cabe2" /> */}
          <DonateFormItem>
            <H4>후원 방법</H4>
            <InputRadioGroup full>
              {(!initialValues.supportTerm ||
                initialValues.supportTerm === SUPPORT_TERM_REGULAR) && (
                <InputRadio
                  name="supportTerm"
                  ref={register}
                  tab
                  value={SUPPORT_TERM_REGULAR}
                  onChange={() => {
                    setValue(`supportAmountInput`, ``);
                    clearErrors(`supportAmountInput`);
                  }}
                  label="정기"
                />
              )}
              {(!initialValues.supportTerm ||
                initialValues.supportTerm === SUPPORT_TERM_ONCE) && (
                <InputRadio
                  name="supportTerm"
                  ref={register}
                  tab
                  value={SUPPORT_TERM_ONCE}
                  onChange={() => {
                    setValue(`supportAmountInput`, ``);
                    clearErrors(`supportAmountInput`);
                  }}
                  label="일시"
                />
              )}
            </InputRadioGroup>
          </DonateFormItem>
          <DonateFormItem>
            <H4>후원 분야</H4>
            <Select
              readOnly={initialValues.supportCategory !== undefined}
              name="supportCategoryIndex"
              ref={register({
                required: {
                  value: true,
                  message: `필수 입력입니다`,
                },
              })}
            >
              <option value="">후원 분야 선택</option>
              {supportCategories
                .filter(
                  (category) =>
                    initialValues.supportCategory !== undefined ||
                    category.solicitCode !== SOLICIT_CODE_CHECKOUT_INMEMORY,
                )
                .map((category) => (
                  <option key={category.indexNumber} value={category.indexNumber}>
                    {category.codeName}
                  </option>
                ))}
            </Select>
            {errors.supportCategoryIndex && (
              <ErrorMessage>{errors.supportCategoryIndex.message}</ErrorMessage>
            )}
          </DonateFormItem>
          <DonateFormItem
            style={{
              marginBottom: `56px`,
            }}
          >
            <TitleWithText>
              <H4>후원 금액(원)</H4>
              <div>※ 원하시는 금액을 선택해 주세요</div>
            </TitleWithText>
            <>
              <InputRadioGroup
                full
                style={{
                  marginBottom: `-1px`,
                }}
              >
                <InputRadio
                  name="supportAmount"
                  ref={register}
                  onFocus={onPressSupportAmount}
                  tab
                  value={
                    selectedAmount?.supportAmount1 ||
                    defaultAmount?.supportAmount1
                  }
                  label={
                    (selectedAmount.supportAmount1 &&
                      selectedAmount.supportAmount1?.toLocaleString()) ||
                    defaultAmount.supportAmount1?.toLocaleString() ||
                    ''
                  }
                />
                <InputRadio
                  name="supportAmount"
                  ref={register}
                  onFocus={onPressSupportAmount}
                  tab
                  value={
                    selectedAmount?.supportAmount2 ||
                    defaultAmount?.supportAmount2
                  }
                  label={
                    (selectedAmount.supportAmount2 &&
                      selectedAmount.supportAmount2?.toLocaleString()) ||
                    defaultAmount.supportAmount2?.toLocaleString() ||
                    ''
                  }
                />
                <InputRadio
                  name="supportAmount"
                  ref={register}
                  onFocus={onPressSupportAmount}
                  tab
                  value={
                    selectedAmount?.supportAmount3 ||
                    defaultAmount?.supportAmount3
                  }
                  label={
                    (selectedAmount.supportAmount3 &&
                      selectedAmount.supportAmount3?.toLocaleString()) ||
                    defaultAmount.supportAmount3?.toLocaleString() ||
                    ''
                  }
                />
              </InputRadioGroup>
              <InputRadioGroup full>
                <InputRadio
                  name="supportAmount"
                  ref={register}
                  onFocus={onPressSupportAmount}
                  tab
                  value={
                    selectedAmount?.supportAmount4 ||
                    defaultAmount?.supportAmount4
                  }
                  label={
                    (selectedAmount.supportAmount4 &&
                      selectedAmount.supportAmount4?.toLocaleString()) ||
                    defaultAmount.supportAmount4?.toLocaleString() ||
                    ''
                  }
                />
                <InputRadio
                  name="supportAmount"
                  ref={register}
                  onFocus={onPressSupportAmount}
                  tab
                  value={
                    selectedAmount?.supportAmount5 ||
                    defaultAmount?.supportAmount5
                  }
                  label={
                    (selectedAmount.supportAmount5 &&
                      selectedAmount.supportAmount5?.toLocaleString()) ||
                    defaultAmount.supportAmount5?.toLocaleString() ||
                    ''
                  }
                />
                <div>
                  <Controller
                    control={control}
                    name="supportAmountInput"
                    rules={{
                      validate: (value) => {
                        if (supportAmount) {
                          return true;
                        }
                        const val = Number(value);
                        if (
                          initialValues.minAmount &&
                          initialValues.minAmount > -1
                        ) {
                          // 250730 - 리워드 캠페인(sdoCode 코드) 통해서 들어올 경우 최소 금액 DB 설정
                          if (val < initialValues.minAmount) {
                            return `${initialValues.minAmount.toLocaleString()}원 이상부터 정기후원이 가능합니다`;
                          }
                        } else {
                          if (
                            supportTerm === SUPPORT_TERM_ONCE &&
                            initialValues.supportCategory === 'D201' &&
                            initialValues.solicitCode === 'CM25M'
                          ) {
                            // 250624 - 운영 요청으로 CM25M 캠페인 최소 금액 설정
                            if (val < 5000) {
                              return `5천원 이상부터 일시후원이 가능합니다`;
                            }
                          }
                          if (
                            supportTerm === SUPPORT_TERM_ONCE &&
                            initialValues.supportCategory === 'D201' &&
                            initialValues.solicitCode === 'DM26A'
                          ) {
                            // 260824 - 운영 요청으로 DM26A 플레이브 캠페인 일시후원 최소 금액 설정
                            if (val < 30000) {
                              return `3만원 이상부터 일시후원이 가능합니다`;
                            }
                          }
                          if (supportTerm === SUPPORT_TERM_REGULAR) {
                            if (
                              initialValues.supportCategory === 'D001' &&
                              initialValues.solicitCode === 'TT25D'
                            ) {
                              // 251124 - 운영 요청으로 TT25D 캠페인 최소 금액 설정
                              if (val < 20000) {
                                return `2만원 이상부터 정기후원이 가능합니다`;
                              }
                            } else if (
                              initialValues.supportCategory === 'D001' &&
                              initialValues.solicitCode === 'CM26K'
                            ) {
                              // 260413 - 운영 요청으로 CM26K 캠페인 최소 금액 설정
                              if (val < 50000) {
                                return `5만원 이상부터 정기후원이 가능합니다`;
                              }
                            } else if (
                              initialValues.supportCategory === 'D001' &&
                              initialValues.solicitCode === 'DM26A'
                            ) {
                              // 260824 - 운영 요청으로 DM26A 플레이브 캠페인 정기후원 최소 금액 설정
                              if (val < 20000) {
                                return `2만원 이상부터 정기후원이 가능합니다`;
                              }
                            } else {
                              if (val < PAYMENT_MIN_AMOUNT) {
                                return `${PAYMENT_MIN_AMOUNT_MSG}`;
                              }
                            }
                          }
                        }
                        if (isExcessAmount(val)) {
                          return excessAmountMsg;
                        }
                        return true;
                      },
                    }}
                    as={
                      <InputNumber
                        onFocus={() => setValue(`supportAmount`, ``)}
                        placeholder="금액 직접 입력"
                        style={{
                          borderTop: `1px solid #1cabe2`,
                          borderLeft: `none`,
                        }}
                        allowNegative={false}
                        maxLength={12}
                      />
                    }
                    type={
                      process.env.DONATE_USE_AMOUNT_DIRECT_INPUT === 'N'
                        ? 'hidden'
                        : 'text'
                    }
                  />
                </div>
              </InputRadioGroup>
            </>
            {errors.supportAmountInput && (
              <ErrorMessage>{errors.supportAmountInput.message}</ErrorMessage>
            )}
            {!errors.supportAmountInput &&
              isExcessAmount(Number(supportAmountInput)) && (
                <ErrorMessage>{excessAmountMsg}</ErrorMessage>
              )}
            {/* WEB-379: 고액후원(sfchannel=MAJ) 안내사항 */}
            {initialValues.sfchannel === 'MAJ' && (
              <NotiBox style={{ marginTop: 16 }}>
                <NotiList>
                  <NotiItem><strong>신한은행 100-019-976130 유니세프한국위원회</strong> 계좌로 직접 이체 가능합니다.</NotiItem>
                  <NotiItem>3천만원 이상의 후원금은 계좌이체로만 후원이 가능합니다.</NotiItem>
                  <NotiItem>문의전화: 02-721-5150~3</NotiItem>
                  <NotiItem>문의 이메일: major@unicef.or.kr</NotiItem>
                </NotiList>
              </NotiBox>
            )}
            <FormTotal>
              <H4>총 후원금액</H4>
              <FormTotalPrice>{finalAmount.toLocaleString()}원</FormTotalPrice>
            </FormTotal>
            <Button full type="submit">
              다음
            </Button>
          </DonateFormItem>
        </form>
      </FormContainer>
    );
  },
);

export default DonateStep1;
